/**
 * useInboxRollup — the single source of truth for the /phone workspace's
 * cross-channel inbox. Consumed by the gold status bar (counts), the
 * Inbox tab (card grid), the person timeline (slide-over), and the
 * "Needs a response <n>" chip in the header.
 *
 * Inputs: recentCalls, voicemails, smsThreads, contactsIndex (all from
 * existing endpoints; the hook does NOT fetch — it composes).
 *
 * Rollup keys entries by 10-digit phone (last10) so cross-channel
 * activity for the same person merges into one row.
 *
 * "Needs a response" definition (PART 0.1 of rebuild spec):
 *   - a missed/unanswered inbound call clears when someone calls back,
 *     texts the number, or marks it handled
 *   - a voicemail clears when played or marked handled
 *   - an inbound text clears when replied to or marked handled
 *   - outbound-only activity is never "needs a response"
 *   - items marked hidden (junk/unnamed) are excluded from the count
 *
 * Staff-to-staff softphone calls whose Twilio "from" is `client:<uuid>`
 * are dropped entirely — they aren't customer inbox items.
 */
import { useMemo } from "react";
import { formatPhoneNumber } from "../../lib/utils";

const digitsOnly = (raw) => String(raw || "").replace(/\D/g, "");
export const last10 = (raw) => digitsOnly(raw).slice(-10);

function _callSummary(c) {
  const dir = c.direction === "inbound" ? "Inbound" : "Outbound";
  if (["no-answer", "missed"].includes(c.status)) return `Missed · ${c.contact_type || "call"}`;
  if (c.status === "busy") return "Busy";
  if (c.status === "failed" || c.status === "canceled") return "Failed";
  if (c.duration_seconds) {
    const s = Math.max(0, Math.floor(c.duration_seconds));
    const d = s < 60 ? `0:${s.toString().padStart(2, "0")}` : `${Math.floor(s/60)}:${(s%60).toString().padStart(2, "0")}`;
    return `${dir} · ${d}`;
  }
  return dir;
}

function _blankInboxEntry(key, phone) {
  return {
    key, phone, ourNumber: "",
    calls: [], voicemails: [], smsThread: null,
    latestTs: 0, latestKind: "", latestSummary: "", latestMissed: false, latestDirection: "",
    needsCallback: false, unheardVm: false, unreadSms: 0, needsRename: false,
    contact: null, displayName: "", contactType: "", hidden: false,
    // Tenant tag surfaced in the inbox card (iter 475). Empty when
    // the underlying rows carry no company_id (legacy pre-tenant
    // rows) — the UI then just skips the badge.
    companyId: "", companyName: "",
    calledBackAfterMiss(afterTs) {
      return this.calls.some(c => c.direction === "outbound" && new Date(c.created_at || 0).getTime() > afterTs);
    },
  };
}

export function useInboxRollup({ recentCalls = [], voicemails = [], smsThreads = [], contactsIndex = {}, hiddenKeys, activeCompanyId = null }) {
  const inboxItems = useMemo(() => {
    const byKey = new Map();

    // Iter 484 — DO NOT merge cross-tenant activity for the same
    // phone number. If number `4145550000` sent SMS to Avida AND
    // called Bylyngo, those are TWO separate inbox rows, not one.
    // The rollup key becomes `last10(phone) + ':' + (company_id||'')`
    // so tenant isolation survives client-side.
    const _rowKey = (phone, cid) => `${last10(phone)}::${cid || "__"}`;

    // When the caller has picked an outbound line (companies-header
    // dropdown), also filter server-side rows to that tenant.
    // `activeCompanyId === null` means "show every tenant" (used by
    // super_admin's "All companies" option).
    const _keep = (rowCid) => {
      if (!activeCompanyId) return true;               // show-all mode
      if (!rowCid) return true;                        // legacy pre-tenant rows
      return rowCid === activeCompanyId;
    };

    for (const c of recentCalls) {
      if (!_keep(c.company_id)) continue;
      const other = c.direction === "inbound" ? c.from_number : c.to_number;
      if (typeof other === "string" && other.startsWith("client:")) continue;
      const digits = last10(other);
      if (!digits) continue;
      const key = _rowKey(other, c.company_id);
      const isMissed = ["no-answer", "missed", "busy", "failed", "canceled"].includes(c.status);
      const entry = byKey.get(key) || _blankInboxEntry(key, other);
      entry.calls.push(c);
      const t = new Date(c.created_at || 0).getTime();
      if (t > entry.latestTs) {
        entry.latestTs = t; entry.latestKind = "call";
        entry.latestSummary = _callSummary(c);
        entry.latestMissed = isMissed; entry.latestDirection = c.direction;
      }
      if (isMissed && c.direction === "inbound" && !c.handled && !entry.calledBackAfterMiss(t)) {
        entry.needsCallback = true;
      }
      if (c.company_id && !entry.companyId) entry.companyId = c.company_id;
      if (c.company_name && !entry.companyName) entry.companyName = c.company_name;
      byKey.set(key, entry);
    }

    for (const v of voicemails) {
      if (!_keep(v.company_id)) continue;
      if (typeof v.from_number === "string" && v.from_number.startsWith("client:")) continue;
      const digits = last10(v.from_number);
      if (!digits) continue;
      const key = _rowKey(v.from_number, v.company_id);
      const entry = byKey.get(key) || _blankInboxEntry(key, v.from_number);
      entry.voicemails.push(v);
      const t = new Date(v.created_at || 0).getTime();
      if (t > entry.latestTs) {
        entry.latestTs = t; entry.latestKind = "vm";
        const s = Math.max(0, Math.floor(v.duration || 0));
        const d = s < 60 ? `0:${s.toString().padStart(2, "0")}` : `${Math.floor(s/60)}:${(s%60).toString().padStart(2, "0")}`;
        entry.latestSummary = `Voicemail · ${d}`;
      }
      if (!v.is_read) entry.unheardVm = true;
      if (v.company_id && !entry.companyId) entry.companyId = v.company_id;
      if (v.company_name && !entry.companyName) entry.companyName = v.company_name;
      byKey.set(key, entry);
    }

    for (const th of smsThreads) {
      if (!_keep(th.company_id)) continue;
      const rawPhone = th.phone_number || th.from_number || th.to_number || "";
      if (typeof rawPhone === "string" && rawPhone.startsWith("client:")) continue;
      const digits = last10(rawPhone);
      if (!digits) continue;
      const key = _rowKey(rawPhone, th.company_id);
      const entry = byKey.get(key) || _blankInboxEntry(key, rawPhone);
      entry.smsThread = th;
      const ts = new Date(
        th.last_message_time || th.last_message_at || th.updated_at || th.created_at || 0
      ).getTime();
      if (ts > entry.latestTs) {
        entry.latestTs = ts; entry.latestKind = "sms";
        entry.latestSummary = (th.last_message || th.last_message_body || th.preview || "Text message").slice(0, 80);
      }
      if ((th.unread_count || 0) > 0) entry.unreadSms = th.unread_count;
      entry.ourNumber = th.our_number || th.company_number || entry.ourNumber;
      // Propagate tenant tag from the SMS thread onto the aggregated
      // inbox entry. Voicemails + calls also carry company_id but SMS
      // is where the tenant leak was most visible (iter 474), so we
      // prefer its value when present.
      if (th.company_id) entry.companyId = th.company_id;
      if (th.company_name) entry.companyName = th.company_name;
      byKey.set(key, entry);
    }

    for (const [, e] of byKey) {
      const c = contactsIndex[last10(e.phone)];
      if (c) {
        e.contact = c;
        const displayName = `${c.first_name || ""} ${c.last_name || ""}`.trim();
        e.displayName = displayName || formatPhoneNumber(e.phone);
        e.contactType = c.contact_type || c.tags?.[0] || "";
        // If a contact exists but carries no usable name (e.g. an
        // SMS-auto-import stub or a placeholder from earlier tooling),
        // the row would show a bare phone number with no way to
        // name it. Treat that as "needs rename" so the Add-Contact
        // (UserPlus) button appears — the modal will PATCH the
        // existing row instead of creating a duplicate.
        if (!displayName) e.needsRename = true;
      } else {
        e.displayName = formatPhoneNumber(e.phone);
        e.needsRename = true;
      }
      e.hidden = hiddenKeys ? hiddenKeys.has(e.key) : false;
    }
    return Array.from(byKey.values()).sort((a, b) => b.latestTs - a.latestTs);
  }, [recentCalls, voicemails, smsThreads, contactsIndex, hiddenKeys, activeCompanyId]);

  // Needs-a-response counts. Hidden entries are excluded (PART 0.1).
  const counts = useMemo(() => {
    let toCallBack = 0, vm = 0, unread = 0;
    for (const e of inboxItems) {
      if (e.hidden) continue;
      if (e.needsCallback) toCallBack += 1;
      if (e.unheardVm) vm += 1;
      if (e.unreadSms && e.unreadSms > 0) unread += 1;
    }
    return { toCallBack, vm, unread, needsResponse: toCallBack + vm + unread };
  }, [inboxItems]);

  return { inboxItems, counts };
}
